import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Trash2, ChevronLeft, Search, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePaywall } from "@/context/PaywallContext";
import { DecreeList } from "@/components/DecreeList";

export default function History() {
  const { user } = useAuth();
  const { openPaywall } = usePaywall();
  const premium = user?.tier === "premium";
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/decrees/history").then((res) => setItems(res.data)).catch(() => setItems([]));
  };

  useEffect(load, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/decrees/${id}`);
    setItems((prev) => prev.filter((i) => i.id !== id));
    if (active?.id === id) setActive(null);
    toast.success("Decree set removed");
  };

  if (active) {
    return (
      <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 pb-24">
        <button
          data-testid="back-to-history"
          onClick={() => setActive(null)}
          className="flex items-center gap-2 text-sm text-[#7A756E] hover:text-[#2C2A29] transition-colors duration-300 mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> Back to history
        </button>
        <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">Decrees for</p>
        <h1 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] tracking-tight capitalize mt-1 mb-8">
          {active.topic}
        </h1>
        <DecreeList session={active} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 pb-24">
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">Your decree history</h1>
      <p className="font-decree text-lg text-[#7A756E] mt-3">Revisit and re-declare what you have established.</p>

      {!premium && (
        <div data-testid="history-free-banner" className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm px-5 py-4">
          <p className="font-body text-sm text-[#4A4744]">
            Free accounts keep the last 25 decree sets from the past 30 days.
          </p>
          <button
            data-testid="history-upgrade-button"
            onClick={() => openPaywall("Go Premium for unlimited, searchable decree history.")}
            className="shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm rounded-full border border-[#9B7E4E] text-[#9B7E4E] hover:bg-[#9B7E4E] hover:text-[#FAF9F6] transition-colors duration-300"
          >
            <Crown className="w-3.5 h-3.5" /> Go Premium
          </button>
        </div>
      )}

      {premium && items && items.length > 0 && (
        <div className="relative mt-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A39C90]" />
          <input
            data-testid="history-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your decrees by topic…"
            className="w-full bg-transparent border border-[#D9D4CC] pl-10 pr-4 py-3 text-base text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300"
          />
        </div>
      )}

      {items === null ? (
        <p className="font-body text-[#7A756E] mt-12">Loading…</p>
      ) : items.length === 0 ? (
        <div data-testid="history-empty" className="text-center border border-dashed border-[#D9D4CC] rounded-sm py-20 mt-10">
          <ScrollText className="w-8 h-8 text-[#B7AE9F] mx-auto" strokeWidth={1.5} />
          <p className="font-decree text-xl text-[#4A4744] mt-5">No decrees yet.</p>
          <Link
            to="/generate"
            data-testid="history-generate-link"
            className="inline-block mt-6 px-6 py-3 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300"
          >
            Generate your first decrees
          </Link>
        </div>
      ) : (
        <ul className="mt-10 space-y-3">
          {items
            .filter((it) => !q.trim() || (it.topic || "").toLowerCase().includes(q.trim().toLowerCase()))
            .map((it) => (
            <li
              key={it.id}
              data-testid={`history-item-${it.id}`}
              onClick={() => setActive(it)}
              className="group flex items-center justify-between gap-4 px-6 py-5 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm hover:bg-[#EAE6DF] hover:border-[#B7AE9F] transition-all duration-300 cursor-pointer"
            >
              <div>
                <p className="font-heading text-xl text-[#2C2A29] capitalize">{it.topic}</p>
                <p className="font-body text-sm text-[#7A756E] mt-1">
                  {it.decrees.length} decrees
                  {it.audience === "person" && it.name ? ` · over ${it.name}` : ""}
                  {it.audience === "group" && it.name ? ` · over ${it.name}` : ""}
                  {it.audience === "self" ? " · for yourself" : ""}
                  {" · "}
                  {new Date(it.created_at).toLocaleDateString()}
                </p>
              </div>
              <button
                data-testid={`delete-history-${it.id}`}
                onClick={(e) => remove(it.id, e)}
                aria-label="Delete"
                className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 text-[#B7AE9F] hover:text-[#9B3B3B] p-2"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
